import { useEffect, useMemo, useState } from "react";

// Shared draft-state hook for min/max range filters (Precio, Superficie, and
// any future numeric range). Consumers pass `state`, the URL/state `key`
// (e.g. "price", "groundSize"), and a `getBounds(state)` to compute
// `{min, max, step}` — dynamic so e.g. Precio can switch its ceiling when
// the operation is rent-only.
//
// Returns the canonical draft surface (`{minDraft, maxDraft, setters,
// bounds, isFullRange, isMaxAtTop, isDirty, commit, reset}`) plus
// `canCommit: true` for symmetry with sibling drafts. Pair with
// `<RangeBody draft={d} ... />` for the presentation.
//
// `commit()` NORMALIZES order via Math.min/max — user may type a higher
// min than max during editing; we want the filter to apply sensibly when
// they hit Aplicar rather than silently dropping the input.
export const useRangeDraft = ({ state, key, getBounds }) => {
    const bounds = useMemo(() => getBounds(state), [getBounds, state]);
    const stateValue = state[key];

    const [minDraft, setMinDraft] = useState(stateValue[0] ?? bounds.min);
    const [maxDraft, setMaxDraft] = useState(stateValue[1] ?? bounds.max);

    useEffect(() => {
        setMinDraft(stateValue[0] ?? bounds.min);
        setMaxDraft(stateValue[1] ?? bounds.max);
    }, [stateValue, bounds.min, bounds.max]);

    const isFullRange = minDraft === bounds.min && maxDraft === bounds.max;
    const isMaxAtTop = maxDraft >= bounds.max;

    const draftShape = isFullRange ? [] : isMaxAtTop ? [minDraft] : [minDraft, maxDraft];

    const isDirty =
        draftShape.length !== stateValue.length || draftShape.some((v, i) => v !== stateValue[i]);

    const commit = (setFilter, clearFilter) => {
        const lo = Math.min(minDraft, maxDraft);
        const hi = Math.max(minDraft, maxDraft);
        if (lo === bounds.min && hi === bounds.max) clearFilter(key);
        else if (hi >= bounds.max) setFilter(key, [lo]);
        else setFilter(key, [lo, hi]);
    };

    const reset = (_setFilter, clearFilter) => {
        setMinDraft(bounds.min);
        setMaxDraft(bounds.max);
        clearFilter(key);
    };

    return {
        minDraft,
        maxDraft,
        setMinDraft,
        setMaxDraft,
        bounds,
        isFullRange,
        isMaxAtTop,
        isDirty,
        canCommit: true,
        commit,
        reset,
    };
};

// Default digit parser. Consumers can pass their own if they need different
// semantics (e.g. accepting decimal points), but the existing filters all
// use this shape.
export const parseDigits = (str) => {
    const digits = String(str).replace(/[^\d]/g, "");
    return digits === "" ? null : Number(digits);
};

export const clamp = (n, min, max) => Math.min(Math.max(n, min), max);
